import { useCallback } from 'react';
import { startCase } from 'lodash';
import { quicktypeJSONSchema } from '../util/quicktype';

export const useDownloadTSConvertedSchema = (allProceduresByScope) => {
  const downloader = useCallback(async () => {
    if (typeof allProceduresByScope !== 'object') {
      throw new Error(
        'useDownloadTSConvertedSchema(allProceduresByScope) did not receive "allProceduresByScope" object',
      );
    }

    let file = `
/** 
    This file was automatically generated from RPC-telemetry-client.
    ${window.location.href} 
    Generated at ${new Date().toString()}
**/

import { RPCClient, CallOptions } from '@therms/rpc-client'

export namespace RPCSchema {
`;

    // { [scope: string]: { [procedure: string]: { [version: string]: string } } }
    // at the end of this file we append the constants with the RPC shorthand reference, ie: `${scope}::${procedure}::${version.version}`
    const rpcMap = {};

    const rpcsByScope = Array.from(Object.entries(allProceduresByScope));

    try {
      for await (const [scope, versionsByProcedure] of rpcsByScope) {
        const scopeFormatted = startCase(scope).split(' ').join('');

        if (!rpcMap[scopeFormatted]) {
          rpcMap[scopeFormatted] = {};
        }

        file += `export namespace ${scopeFormatted} {\n`;

        for await (const [procedure, versions] of Object.entries(
          versionsByProcedure,
        )) {
          const procedureFormatted = startCase(procedure).split(' ').join('');

          if (!rpcMap[scopeFormatted][procedureFormatted]) {
            rpcMap[scopeFormatted][procedureFormatted] = {};
          }

          file += `export namespace ${procedureFormatted} {\n`;

          for await (const version of versions) {
            if (
              !rpcMap[scopeFormatted][procedureFormatted][`v${version.version}`]
            ) {
              rpcMap[scopeFormatted][procedureFormatted][
                `v${version.version}`
              ] = `${scope}::${procedure}::${version.version}`;
            }

            file += `export namespace v${version.version} {\n`;

            if (version.args) {
              const { lines } = await quicktypeJSONSchema({
                targetLanguage: 'typescript',
                typeName: `Args`,
                jsonSchemaString: JSON.stringify(version.args),
              }).catch((err) => {
                console.warn(
                  `quicktypeJSONSchema() unable to convert: ${scope}::${procedure}::v${version.version}::Args`,
                  {
                    scope,
                    procedure,
                    version,
                  },
                );

                throw err;
              });

              file += lines.join('\n') + '\n';
            } else {
              file += `export type Args = void;\n`;
            }

            if (version.data) {
              const { lines } = await quicktypeJSONSchema({
                targetLanguage: 'typescript',
                typeName: `Data`,
                jsonSchemaString: JSON.stringify(version.data),
              }).catch((err) => {
                console.warn(
                  `quicktypeJSONSchema() unable to convert: ${scope}::${procedure}::v${version.version}::Data`,
                  {
                    scope,
                    procedure,
                    version,
                  },
                );

                throw err;
              });

              file += lines.join('\n') + '\n';
            } else {
              file += `export type Data = void;\n`;
            }

            file += `} // end of v${version.version} namespace\n\n`;
          }

          file += `} // end of ${procedureFormatted} namespace\n\n`;
        }

        file += `} // end of ${scopeFormatted} namespace\n\n`;
      }

      file += '}\n';

      file += `\n\n`;

      file += `

// RPC call shorthand constants

export const RPCRequests = ${JSON.stringify(rpcMap, null, 2)}       
`;

      let rpcPrebuiltCaller = `
/**
  The "rpc" export is code-generated with all args/data typings constructed for a great autocomplete DX in any TS project. 
  
  The "rpc" calls rely on an instance of the RPCClient constructor that is exported from npm pkg "@therms/rpc-client".
**/
 
export type CallReturnType<Data = unknown> = Promise<{
  code: number
  data: Data
  message?: string
  success: boolean
}>

export const RPCService = (rpcClient: RPCClient) => ({
`;

      Object.keys(rpcMap).forEach((scope) => {
        rpcPrebuiltCaller += `${scope}: {`;

        Object.keys(rpcMap[scope]).forEach((procedure) => {
          rpcPrebuiltCaller += `${procedure}: {`;

          Object.keys(rpcMap[scope][procedure]).forEach((version) => {
            rpcPrebuiltCaller += `${version}: {
    call: (args: RPCSchema.${scope}.${procedure}.${version}.Args, opts?: CallOptions): CallReturnType<RPCSchema.${scope}.${procedure}.${version}.Data> => rpcClient.call(RPCRequests.${scope}.${procedure}.${version}, args, opts),
},\n
`;
          });

          rpcPrebuiltCaller += `},\n`;
        });

        rpcPrebuiltCaller += `},\n`;
      });

      rpcPrebuiltCaller += `}) // RPCService`;

      file += `\n\n`;
      file += rpcPrebuiltCaller;

      const element = document.createElement('a');

      element.setAttribute(
        'href',
        'data:text/plain;charset=utf-8,' + encodeURIComponent(file),
      );
      element.setAttribute('download', 'rpc-docs-schema.ts');

      element.style.display = 'none';
      document.body.appendChild(element);

      element.click();

      document.body.removeChild(element);
    } catch (err) {
      console.warn(
        'useDownloadTSConvertedSchema(allProceduresByScope) unable to convert using quicktype. Be sure that the passed in data "allProceduresByScope" didnt change unexpectedly. ',
        allProceduresByScope,
      );
      console.error(err);
      alert(
        'Unable to convert and download schema, check console for more details.',
      );
    }
  }, [allProceduresByScope]);

  return downloader;
};
